import { Fragment, memo, useMemo } from 'react';
import { Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PROVIDER_TYPE_ENUM } from 'modules/inventory/provider/common/constants/provider.types';
import { useProviderFieldsByType } from 'modules/inventory/settings/provider-field/hooks/useProviderFieldsByType';
import { IProviderField } from 'modules/inventory/settings/provider-field/interfaces/IProviderField';
import DynamicFieldFormField from './DynamicFieldFormField';

type FieldGroup = { providerType: string; fields: IProviderField[] };

type DynamicFieldsSectionProps = {
  providerType: PROVIDER_TYPE_ENUM;
};

const DynamicFieldsSection = ({ providerType }: DynamicFieldsSectionProps) => {
  const { t } = useTranslation('providerField');
  const { data: fields, isLoading } = useProviderFieldsByType(providerType);

  const groups = useMemo<FieldGroup[]>(() => {
    if (!fields?.length) return [];
    const map = new Map<string, FieldGroup>();
    for (const field of fields) {
      const key = field.providerType as string;
      if (!map.has(key)) map.set(key, { providerType: key, fields: [] });
      (map.get(key) as FieldGroup).fields.push(field);
    }
    return Array.from(map.values());
  }, [fields]);

  const showGroupHeaders = groups.length > 1;

  if (isLoading || !groups.length) return null;

  return (
    <Grid container spacing={{ xs: 1, md: 2 }} columns={{ xs: 4, sm: 8, md: 12 }}>
      {groups.map((group, idx) => (
        <Fragment key={group.providerType}>
          {showGroupHeaders && (
            <Grid item xs={12}>
              {idx > 0 && <Divider sx={{ mb: 1 }} />}
              <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 0.5 }}>
                {t(`PROVIDER_TYPE.${group.providerType}`)}
              </Typography>
            </Grid>
          )}
          {group.fields.map((field) => (
            <DynamicFieldFormField key={field._id} field={field} />
          ))}
        </Fragment>
      ))}
    </Grid>
  );
};

export default memo(DynamicFieldsSection);
